import React, { useContext } from "react";
import { SettingsContext } from "./SettingsContext.tsx";
import { SelectButton } from "primereact/selectbutton";
import { ToggleButton } from "primereact/togglebutton";
import { isElapsedTimeQuestion, TimeDisplayOptions, TimeIntervalOptions, TimeQuestionOptions } from "../Constants.ts";
import { withLabels } from "../Labels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * Which way round a clock question is asked, and how precise the times are.
 *
 * The interval is the difficulty dial, and its order is the order telling the
 * time is taught: o'clock, then half past, then quarters, then five minutes,
 * then every minute.
 */
export function TimeSettings() {
    const { settings, setSettings } = useContext(SettingsContext);

    function handleQuestionChange(value: TimeQuestionOptions) {
        // SelectButton hands back null when the selected option is clicked
        // again; a sheet has to ask something.
        if (!value) {
            return;
        }
        setSettings({ ...settings, TimeQuestion: value });
    }

    function handleIntervalChange(value: TimeIntervalOptions) {
        if (!value) {
            return;
        }
        setSettings({ ...settings, TimeInterval: value });
    }

    function handleDisplayChange(value: TimeDisplayOptions) {
        if (!value) {
            return;
        }
        setSettings({ ...settings, TimeDisplay: value });
    }

    function handleCrossNoonToggle(value: boolean) {
        setSettings({ ...settings, CrossNoon: value });
    }

    const elapsed = isElapsedTimeQuestion(settings.TimeQuestion);
    const t = useTranslation();

    return (
        <div className="timeSettingsContainer">
            <div className="regroupingContainer">
                <SelectButton
                    value={settings.TimeQuestion}
                    onChange={(e) => handleQuestionChange(e.value)}
                    options={withLabels(Object.values(TimeQuestionOptions), settings.Language)}
                    className="select regrouping"
                    aria-label={t('settings.questionType')}
                />
            </div>

            {/* How the two times are shown - only meaningful for an elapsed
                question, so hidden (not disabled) otherwise. */}
            {elapsed && (
                <div className="regroupingContainer">
                    <span className="settingLabel">{t('settings.showTimesAsLabel')}</span>
                    <SelectButton
                        value={settings.TimeDisplay}
                        onChange={(e) => handleDisplayChange(e.value)}
                        options={withLabels(Object.values(TimeDisplayOptions), settings.Language)}
                        className="select regrouping"
                        aria-label={t('settings.timeDisplayAria')}
                    />
                </div>
            )}

            {/* Every problem crosses noon, with a.m. and p.m. (ADR 0090 §3).
                Part of the format's own panel, so basic (ADR 0023 §2), and
                elapsed questions only, like the display above. */}
            {elapsed && (
                <div className="remainderPagesContainer">
                    <label htmlFor="crossNoon">{t('settings.crossNoonLabel')}</label>
                    <ToggleButton
                        onLabel={t('settings.toggleYes')}
                        offLabel={t('settings.toggleNo')}
                        inputId="crossNoon"
                        className="remainderToggle"
                        checked={settings.CrossNoon === true}
                        onChange={(e) => handleCrossNoonToggle(e.value)}
                    />
                </div>
            )}
            <div className="selectContainer">
                <span className="settingLabel">{t('settings.timesToTheLabel')}</span>
            </div>
            <div className="regroupingContainer">
                <SelectButton
                    value={settings.TimeInterval}
                    onChange={(e) => handleIntervalChange(e.value)}
                    options={withLabels(Object.values(TimeIntervalOptions), settings.Language)}
                    className="select regrouping"
                    aria-label={t('settings.timeIntervalAria')}
                />
            </div>
        </div>
    );
}

export default TimeSettings;
